import { AppSidebar } from '@/components/sidenav/app-sidebar';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { SidebarInset } from '@/components/ui/sidebar';
import { PersistedSidebarProvider } from '@/components/sidenav/persisted-sidebar-provider';
import { AppProviders } from '@/context/app-providers';
import { useBreadcrumbOverrides } from '@/context/breadcrumb-override.context';
import { HeaderTitleSlot } from '@/context/header-title.context';
import { useHeaderTitleClaims } from '@/context/header-title.hooks';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  buildCrumbTrail,
  resolveCrumbSearch,
} from '@/pages/authenticated/crumbTrail';
import { rememberCrumbSearch } from '@/lib/crumbSearchMemory';
import { useRouteMessage } from '@/routing/route-messages';
import { AgentPanelProvider } from '@/context/agent-panel.context';
import { AgentStateProvider } from '@/context/agent-state.context';
import { AgentContainerProvider } from '@/context/agent-container.context';
import { AnnotationCanvasProvider } from '@/context/annotation-canvas.context';
import { BackgroundJobsPanel } from '@/components/background-jobs/BackgroundJobsPanel';
import { TodoListProvider } from '@/components/common/chat/todo-list';
import { ModuleChatAgentRenderer as ChatAgentRenderer } from '@/components/common/ModuleChatAgentRenderer';
import { HotkeysProvider } from 'react-hotkeys-hook';
import { EntityQuickFind } from '@/components/common/EntityQuickFind';
import { AnnotationCanvas } from '@/components/common/chat/AnnotationCanvas';
import ErrorBoundary from 'src/components/ErrorBoundary';
import {
  createRoute,
  notFound,
  Outlet,
  redirect,
  useRouterState,
} from '@tanstack/react-router';
import { Route as rootRoute, type RouterContext } from 'src/pages/__root';
import NotFoundPage from '../NotFoundPage';
import type { ProductModule } from '@/hooks/organization-config/useOrganizationEnabledModules';
import type { FeatureFlagKey } from '@erp/shared-types';
import {
  isBooleanFeatureFlagEnabled,
  useFeatureFlag,
} from '@/integrations/feature-flags/feature-flags';
import React, { useContext } from 'react';
import { cn } from '@/lib/utils';
import { Link } from '@tanstack/react-router';
import { AuthContext } from '@/context/auth.context';
import { useViewMode } from '@/context/view-mode.context';
import { isAdminToolsWhileImitatingEnabled } from '@/context/adminToolsMirror';
import { isAdmin, isAdminToolsVisible } from '@/utils/user';
import { WarningIcon } from '@phosphor-icons/react';
import config from '@/config';
import { Button } from '@/components/ui/button';
import { Action, Subject } from '@erp/authz';
import { useCan } from '@/hooks/useCan';
import { headerNavLinks } from './headerNavLinks';
import { T } from 'gt-react';
import { FeedbackButton } from '@/components/common/FeedbackButton';
import { DeploymentEnvBanner } from '@/components/common/DeploymentEnvBanner';
import { ImitationBanner } from '@/components/common/ImitationBanner';
import { FloatingAgentPanel } from '@/components/common/FloatingAgentPanel';
import { NamiPanel } from '@/components/nami/NamiPanel';
import { HelpMenuProvider } from '@/components/common/HelpMenu';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { useSessionHeartbeat } from '@/hooks/useSessionHeartbeat';
import { usePageUsageTracker } from '@/hooks/page-usage/usePageUsageTracker';
import { FeedbackNavLink } from '@/components/header/FeedbackNavLink';

export function requireModules({
  allOf,
  oneOf,
  context,
}: {
  context: RouterContext;
  allOf?: ProductModule[];
  oneOf?: ProductModule[];
}): void {
  const { isModuleEnabled } = context;

  // require all modules in modulesRequired
  if (allOf) {
    for (const module of allOf) {
      if (!isModuleEnabled(module)) {
        throw notFound({
          routeId: '/authenticated',
          data: {
            message: `Missing required module: ${module}`,
          },
        });
      }
    }
  }

  if (!oneOf) {
    return;
  }

  const hasAtLeastOneModuleEnabled = oneOf?.some(isModuleEnabled);
  if (!hasAtLeastOneModuleEnabled) {
    throw notFound({
      routeId: '/authenticated',
      data: {
        message: `Missing at least one of the following modules: ${oneOf?.join(
          ', ',
        )}`,
      },
    });
  }
}

/**
 * Require a feature flag to be enabled to reach a route. Pair with
 * `access.featureFlag` (which hides the nav entry) so this also blocks
 * direct-URL access. Reads the synchronous flag value (PostHog + local
 * overrides) so it works inside `beforeLoad`.
 */
export function requireFeatureFlag(flag: FeatureFlagKey): void {
  if (!isBooleanFeatureFlagEnabled(flag, false)) {
    throw notFound({
      routeId: '/authenticated',
      data: {
        message: `Feature not enabled: ${flag}`,
      },
    });
  }
}

/**
 * Blocks the /preview/* pitch pages when the `hide-feature-signups` kill
 * switch is on. Absent/off (the default) leaves the waitlist pages open.
 */
export function requireFeatureSignupsVisible(): void {
  if (isBooleanFeatureFlagEnabled('hide-feature-signups', false)) {
    throw redirect({ to: '/' });
  }
}

/**
 * Where to send a user who can't reach the real Agents / Workflows page.
 * Points at the pitch page unless signups are hidden, in which case it falls
 * back to the pre-teaser bounce destination (`/`).
 */
export function featureSignupFallbackRedirect(
  previewPath: '/preview/agents' | '/preview/workflows',
): string {
  return isBooleanFeatureFlagEnabled('hide-feature-signups', false)
    ? '/'
    : previewPath;
}

/**
 * Require user to have a specific permission to access a route.
 *
 * This performs an async permission check against the backend.
 * Use this in beforeLoad for routes that require specific permissions.
 *
 * @example
 * ```typescript
 * beforeLoad: async ({ context }) => {
 *   await requirePermission({
 *     context,
 *     action: Action.Update,
 *     subject: Subject.UserGroup,
 *   });
 * },
 * ```
 */
export async function requirePermission({
  context,
  action,
  subject,
  redirectTo = '/unauthorized',
}: {
  context: RouterContext;
  action: Action;
  subject: Subject;
  redirectTo?: string;
}): Promise<void> {
  const { authzClient } = context;

  const allowed = await authzClient.can(action, subject);

  if (!allowed) {
    throw redirect({
      to: redirectTo,
    });
  }
}

/**
 * Require user to have at least one of the specified permissions.
 *
 * @example
 * ```typescript
 * beforeLoad: async ({ context }) => {
 *   await requireAnyPermission({
 *     context,
 *     permissions: [
 *       { action: Action.Read, subject: Subject.UserGroup },
 *       { action: Action.Update, subject: Subject.UserGroup },
 *     ],
 *   });
 * },
 * ```
 */
export async function requireAnyPermission({
  context,
  permissions,
  redirectTo = '/unauthorized',
}: {
  context: RouterContext;
  permissions: { action: Action; subject: Subject }[];
  redirectTo?: string;
}): Promise<void> {
  const { authzClient } = context;

  for (const { action, subject } of permissions) {
    const allowed = await authzClient.can(action, subject);
    if (allowed) {
      return; // At least one permission is granted
    }
  }

  // No permissions granted
  throw redirect({
    to: redirectTo,
  });
}

/**
 * Require user to be a non-restricted user.
 * Restricted users can only access shared links and shared reports.
 */
export function requireNonRestrictedUser({
  context,
  location,
}: {
  context: RouterContext;
  location: { pathname: string };
}): void {
  const { auth } = context;
  const isRestrictedUser = auth.currentUser?.isRestrictedUser ?? false;

  if (!isRestrictedUser) {
    return;
  }

  // Allow access to shared links, shared reports, and user preferences (so they can change passwords etc)
  const allowedPaths = ['/shared-links', '/shared/', '/settings/user'];
  const isAllowedPath = allowedPaths.some(
    (path) => location.pathname === path || location.pathname.startsWith(path),
  );

  if (!isAllowedPath) {
    throw redirect({
      to: '/shared-links',
    });
  }
}

/**
 * Require user to be an admin.
 * Redirects non-admin users to the specified path (defaults to '/').
 *
 * @example
 * ```typescript
 * beforeLoad: ({ context }) => {
 *   requireAdmin({ context });
 * },
 * ```
 *
 * @example
 * ```typescript
 * beforeLoad: ({ context }) => {
 *   requireAdmin({
 *     context,
 *     redirectTo: '/inbound-emails',
 *   });
 * },
 * ```
 *
 * @example
 * ```typescript
 * // Read-only diagnostic page an imitating admin may opt back into.
 * beforeLoad: ({ context }) => {
 *   requireAdmin({ context, allowImitationAdminTools: true });
 * },
 * ```
 */
export function requireAdmin({
  context,
  redirectTo = '/',
  allowImitationAdminTools = false,
}: {
  context: RouterContext;
  redirectTo?: string;
  /**
   * Opt this route into the ImitationBanner's "Admin tools" switch, so a
   * Gently admin imitating a user can still reach it. Set it ONLY on
   * read-only diagnostic surfaces: `/auth/me` reports the imitated user, so
   * anything whose data calls check the effective user will render and then
   * 403. Off by default — most admin routes should stay hidden.
   */
  allowImitationAdminTools?: boolean;
}): void {
  const { auth } = context;

  const allowed = allowImitationAdminTools
    ? isAdminToolsVisible(auth.currentUser, isAdminToolsWhileImitatingEnabled())
    : auth.currentUser?.isAdmin === true;

  if (!allowed) {
    throw redirect({
      to: redirectTo,
    });
  }
}

/**
 * Gently staff (`isAdmin`) OR a holder of the given permission — the client
 * mirror of the backend `requireAdminOr` bridge guard, for routes that used to
 * be admin-only and now also open to an RBAC grant. The explicit `isAdmin`
 * short-circuit matters: `authzClient.can` has no staff bypass for ordinary
 * subjects, so `requirePermission` alone would lock staff out.
 */
export async function requireAdminOrPermission({
  context,
  action,
  subject,
  redirectTo = '/unauthorized',
}: {
  context: RouterContext;
  action: Action;
  subject: Subject;
  redirectTo?: string;
}): Promise<void> {
  if (context.auth.currentUser?.isAdmin === true) return;
  await requirePermission({ context, action, subject, redirectTo });
}

export const Route = createRoute({
  id: 'authenticated',
  getParentRoute: () => rootRoute,
  beforeLoad: async (ctx) => {
    const { auth } = ctx.context;
    if (!auth.currentUser) {
      throw redirect({
        to: '/login',
        search: {
          // Save current location for redirect after login
          redirect: ctx.location.href,
        },
      });
    }

    // Redirect restricted users to shared-links if they try to access other routes
    requireNonRestrictedUser({
      context: ctx.context,
      location: ctx.location,
    });
  },
  component: () => (
    <AuthenticatedLayout>
      <Outlet />
    </AuthenticatedLayout>
  ),
  notFoundComponent: () => (
    <AuthenticatedLayout>
      <NotFoundPage />
    </AuthenticatedLayout>
  ),
});

function HeaderActions() {
  const { currentUser } = useContext(AuthContext);
  const { viewMode } = useViewMode();
  const admin = isAdmin(currentUser, viewMode);
  const isRestrictedUser = currentUser?.isRestrictedUser ?? false;
  const [canReadBackgroundAgents] = useCan(
    Action.Read,
    Subject.BackgroundAgent,
  );
  // Kill switch: when ON, restore pre-teaser header (signup-only tabs gone,
  // Workflows admin-only). Absent/OFF keeps the growth tabs.
  const { enabled: hideFeatureSignups } = useFeatureFlag(
    'hide-feature-signups',
    false,
  );

  const [canReadTasks] = useCan(Action.Read, Subject.Task);
  const { enabled: tasksEnabled } = useFeatureFlag('tasks-enabled', false);
  const nav = headerNavLinks({
    admin,
    isRestrictedUser,
    canReadBackgroundAgents,
    canReadTasks,
    tasksEnabled,
    hideFeatureSignups,
  });

  return (
    <div className="flex items-center gap-1 px-4">
      {!isRestrictedUser && (
        <>
          {nav.agents && (
            <Button variant="ghost" size="sm" asChild>
              <Link to={nav.agents}>
                <T context="top navbar agents link label">Agents</T>
              </Link>
            </Button>
          )}
          {nav.tasks && (
            <Button variant="ghost" size="sm" asChild>
              <Link to={nav.tasks}>
                <T context="top navbar tasks link label">Tasks</T>
              </Link>
            </Button>
          )}
          <FeedbackNavLink />
          {nav.workflows && (
            <Button variant="ghost" size="sm" asChild>
              <Link to={nav.workflows}>
                <T context="top navbar workflow link label">Workflows</T>
              </Link>
            </Button>
          )}
        </>
      )}
      <NotificationBell />
    </div>
  );
}

/**
 * The header breadcrumb. Kept separate from `AuthenticatedLayout` so it can
 * read `useBreadcrumbOverrides()` — the provider is inside `AppProviders`,
 * which the layout renders.
 */
function AppBreadcrumbs() {
  const matches = useRouterState({ select: (s) => s.matches });
  const location = useRouterState({ select: (s) => s.location });
  // Remember what each page was shown with so its crumb can restore it later.
  React.useEffect(() => {
    rememberCrumbSearch(location.pathname, location.search);
  }, [location.pathname, location.search]);
  const breadcrumbs = buildCrumbTrail({
    matches,
    overrides: useBreadcrumbOverrides(),
    titleClaims: useHeaderTitleClaims(),
    routeMessage: useRouteMessage(),
  });

  return (
    <Breadcrumb>
      <BreadcrumbList className="flex-nowrap overflow-hidden">
        {breadcrumbs.map((breadcrumb, index) => (
          <React.Fragment key={`${breadcrumb.path}:${index}`}>
            <BreadcrumbItem className="min-w-0">
              <CrumbDescription description={breadcrumb.description}>
                {breadcrumb.titleAnchor ? (
                  <HeaderTitleSlot
                    anchor={breadcrumb.titleAnchor}
                    current={index === breadcrumbs.length - 1}
                  />
                ) : (
                  <BreadcrumbLink asChild>
                    <CrumbLink
                      path={breadcrumb.path}
                      search={resolveCrumbSearch(breadcrumb)}
                      className={
                        index === breadcrumbs.length - 1
                          ? cn(
                              'block max-w-[min(18rem,36vw)] truncate',
                              breadcrumb.current &&
                                'font-medium text-foreground',
                            )
                          : 'block max-w-[min(14rem,28vw)] truncate'
                      }
                    >
                      {breadcrumb.title}
                    </CrumbLink>
                  </BreadcrumbLink>
                )}
              </CrumbDescription>
            </BreadcrumbItem>
            {index < breadcrumbs.length - 1 && <BreadcrumbSeparator />}
          </React.Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

/** Explains a layout's crumb in a tooltip when its route declares a description. */
// react-doctor-disable-next-line react-doctor/no-multi-comp
function CrumbDescription({
  description,
  children,
}: {
  description: string | undefined;
  children: React.ReactElement;
}) {
  if (!description) return children;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="flex min-w-0">{children}</span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{description}</TooltipContent>
    </Tooltip>
  );
}

function CrumbLink({
  path,
  search,
  className,
  children,
}: {
  path: string;
  search?: Record<string, unknown>;
  className?: string;
  children: React.ReactNode;
}) {
  const fromPage =
    typeof search?.fromPage === 'string' ? search.fromPage : undefined;
  const taskId = path.startsWith('/tasks/')
    ? path.slice('/tasks/'.length)
    : undefined;

  if (fromPage && taskId && !taskId.includes('/')) {
    return (
      <Link
        to="/tasks/$taskId"
        params={{ taskId }}
        search={{ fromPage }}
        className={className}
      >
        {children}
      </Link>
    );
  }

  // Crumb paths are runtime strings, so the router cannot type their search.
  return (
    <Link
      to={path}
      search={search as Record<string, undefined> | undefined}
      className={className}
    >
      {children}
    </Link>
  );
}

function AuthenticatedLayout({ children }: { children: React.ReactNode }) {
  // Track time-in-app: periodic activity heartbeat while the tab is visible.
  useSessionHeartbeat();
  // Learn which pages this user opens, for the personalized Home page.
  usePageUsageTracker();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isShipAndDebit =
    pathname === '/ship-and-debit' || pathname.startsWith('/ship-and-debit/');

  return (
    <HotkeysProvider>
      <AppProviders>
        <AgentPanelProvider>
          <AgentStateProvider>
            <TodoListProvider>
              <AgentContainerProvider>
                <HelpMenuProvider>
                  <AnnotationCanvasProvider>
                    <PersistedSidebarProvider>
                      <AppSidebar />
                      <SidebarInset
                        // Clip, not hidden: a hidden box still scrolls when focus or
                        // scrollIntoView reaches past its edge, sliding the header off.
                        className="h-svh overflow-clip bg-sidebar transition-[padding] duration-200 ease-in-out"
                        style={{
                          paddingRight: 'var(--agent-panel-width, 0px)',
                        }}
                      >
                        {/* In-flow and above the header on purpose: while
                            imitating, this is the most important thing on the
                            screen and must never be overlapped or scrolled
                            past. Renders nothing when nobody is imitating. */}
                        <ImitationBanner />
                        <header className="bg-sidebar flex h-16 shrink-0 items-center justify-between gap-2 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12">
                          <div
                            id="app-page-header"
                            className="flex min-w-0 items-center gap-2 px-4"
                          >
                            <AppBreadcrumbs />
                          </div>

                          <HeaderActions />
                        </header>

                        {config.isDev && isShipAndDebit && (
                          <div className="mx-2 mb-2 flex items-center gap-2 rounded-md border border-yellow-300 bg-yellow-50 px-3 py-2 text-sm font-medium text-yellow-800 md:ml-0">
                            <WarningIcon
                              className="size-4 shrink-0"
                              weight="fill"
                            />
                            <T context="warning banner shown in dev environment for ship-and-debit section">
                              This is a dev environment — data and actions here
                              do not affect production.
                            </T>
                          </div>
                        )}
                        {/* id is referenced by scrollToTopSelectors in index.tsx
                            so navigation resets this scroller, not the window.
                            It is inset in the sidebar-colored frame the nav
                            and header form. */}
                        <main
                          id="app-main-content"
                          className="flex-1 min-h-0 p-2 min-w-0 overflow-x-hidden overflow-y-auto w-auto mb-2 mr-2 ml-2 md:ml-0 rounded-xl border border-sidebar-border bg-background"
                        >
                          <ErrorBoundary>{children}</ErrorBoundary>
                        </main>
                        <FeedbackButton />
                      </SidebarInset>
                      <EntityQuickFind />
                      <FloatingAgentPanel />
                      {/* Keep the Nami panel mounted without the floating pill. */}
                      <NamiPanel />
                      {/* Fixed, bottom-right, and outside SidebarInset so work
                          started on any page keeps reporting after the user
                          navigates away from it. */}
                      <BackgroundJobsPanel />
                      {/* Fixed overlay — outside SidebarInset so it never
                          occupies layout space or pushes the page down, but
                          inside SidebarProvider so its minimized state can
                          inherit --sidebar-width and track the sidenav's
                          collapse. */}
                      <DeploymentEnvBanner />
                    </PersistedSidebarProvider>
                    <AnnotationCanvas />
                    <ChatAgentRenderer />
                  </AnnotationCanvasProvider>
                </HelpMenuProvider>
              </AgentContainerProvider>
            </TodoListProvider>
          </AgentStateProvider>
        </AgentPanelProvider>
      </AppProviders>
    </HotkeysProvider>
  );
}
